import Button from 'components/_UI/Button';
import InfoTooltip from 'components/_UI/InfoTooltip';
import LinkPage from 'components/_UI/LinkPage';
import Text from 'components/_UI/Text';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import date from 'helpers/date';
import { getProducerProfileImage } from 'helpers/profileImage';
import types from 'helpers/system/types';
import CronogramModal from 'modules/events/detail/CronogramModal';
import React, { useState } from 'react'
import { Calendar, MapPin } from 'lucide-react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import fonts from 'theme/fonts';
import Icon from 'components/_UI/Icon';

function formatSchedule(item) {
    const start = date.getTimeWithoutSeconds(item?.start)
    if (!start) return ''

    const end = item?.end ? ` até ${date.getTimeWithoutSeconds(item.end)}` : ''
    return ` - ${start}${end}`
}

export default function PageCover({ data, participants = [] }) {
    const textIa = 'kaue.ia'

    const [fullCronogramModal, setFullCronogramModal] = useState(false)

    return (
        <Container>
            <BluredBackground src={data?.images[0] || '/assets/images/placeholder-events.png'} />

            <ImageContainer>
                {
                    data?.images[0] ? (
                        <StyledBackground src={data?.images[0]} style={{position: 'relative'}} />
                    ) : <StyledBackground src={'/assets/images/placeholder-events.png'} style={{position: 'relative'}} />
                }

                {data?.is_past && (
                    <FinishedEvent />
                )}
            </ImageContainer>


            <CardMainContent>
                <EventName>
                    <Text
                        color={colors.nightDark}
                        name='subtitle'
                        type='bold'
                        paddingTop={'12px'}
                        paddingBottom={'6px'}
                    >
                        {data?.name}
                    </Text>
                </EventName>

                <CategoriesInfos>
                    <EventType type={data?.classification?.name}>
                        <Text
                            paddingBottom={0}
                            type='bold'
                            color={colors.neutral}
                            name='small'
                        >
                            {types.getBySlug(data?.classification?.name)?.label}
                        </Text>
                    </EventType>

                    {
                        data?.adulthood && (
                            <Tag adulthood>
                                <Text
                                    color={colors.neutralLight}
                                    type='bold'
                                    name='small'
                                > +18
                                </Text>
                            </Tag>
                        )
                    }

                    {data?.tags?.map(el => (
                        <Tag key={el.name}>
                            <Text name='small' color={colors.neutralLight} type='bold'>
                                {el.name}
                            </Text>
                        </Tag>
                    ))}
                </CategoriesInfos>

                <br />

                {
                    !data?.hide_address && (
                        <ContentInfoRow address>
                            <IconContainer>
                                <MapPin color={colors.night} size={24} style={{ marginRight: 10 }} />
                            </IconContainer>
                            <ContentLinkAddress>
                                {
                                    data?.address ? (
                                        <LinkPage isWrapper to={data.link_map}>
                                            <Text color={colors.primaryDark} decoration='underline' paddingBottom={'0px'}>
                                                {
                                                    data?.address?.raw_address ?
                                                        data.address.raw_address :
                                                        (
                                                            <>
                                                                {data?.address?.street?.name && `${data?.address?.street?.name},`} {data?.address?.street?.district && `Bairro ${data?.address?.street?.district}`}
                                                                {data.address.number && `, nº ${data.address.number}`}
                                                                {data.address.complement && ` - ${data.address.complement}`}
                                                            </>
                                                        )
                                                }
                                            </Text>
                                        </LinkPage>
                                    ) : (
                                        <Text color={colors.night} paddingBottom={'0px'}>
                                            Local a definir
                                        </Text>
                                    )
                                }
                            </ContentLinkAddress>
                        </ContentInfoRow>
                    )
                }

                <ContentInfoRow single={data.dates.length === 1}>
                    <IconContainer>
                        <Calendar color={colors.night} size={24} />
                    </IconContainer>

                    <DateContainer>
                        {
                            data.dates.length > 1 ? (
                                <>
                                    <EventDate>
                                        <Text
                                            color={colors.nightDark}>
                                            <b>Dia 1</b> - {date.getDay(data?.dates[0].date)}
                                            &nbsp;de {date.getMounthName(data?.dates[0].date)}
                                            {formatSchedule(data?.dates[0])}
                                        </Text>

                                        <Text color={colors.nightDark}>
                                            <b>Dia 2</b> - {date.getDay(data?.dates[1].date)}
                                            &nbsp;de {date.getMounthName(data?.dates[1].date)}
                                            {formatSchedule(data?.dates[1])}
                                        </Text>
                                    </EventDate>

                                    {data.dates.length >= 3 && (
                                        <CronogramContainer>
                                            <Button
                                                category='outline'
                                                size='medium'
                                                onClick={() => setFullCronogramModal(true)}
                                            >
                                                Ver cronograma completo
                                            </Button>
                                            <CronogramModal
                                                data={data.dates}
                                                visible={fullCronogramModal}
                                                setVisible={setFullCronogramModal}
                                            />
                                        </CronogramContainer>
                                    )}
                                </>
                            ) : (
                                <OnlyOnedate>
                                    <Text color={colors.nightDark}>
                                        {date.getDay(data?.dates[0].date)}
                                        &nbsp;de {date.getMounthName(data?.dates[0].date)}
                                        {formatSchedule(data?.dates[0])}
                                    </Text>
                                </OnlyOnedate>
                            )
                        }
                    </DateContainer>

                </ContentInfoRow>

                {
                    data?.producer_profile?.name && (
                        <ContainerOrganizerLink>
                            <ContentOrganizer>
                                <DynamicProfileImage size='24px' img={getProducerProfileImage(data?.producer_profile)} word={data?.producer_profile?.name} />
                                <Text color={colors.nightDark} paddingBottom={'0'} marginLeft={'10px'} marginRight={'5px'}>Produzido por</Text>
                                <Text color={colors.nightDark} type='bold' paddingBottom={'0px'}>{data?.producer_profile?.name}</Text>
                            </ContentOrganizer>
                        </ContainerOrganizerLink>
                    )
                }

                {
                    data?.establishment?.name && (
                        <ContainerOrganizerLink>
                            <LinkPage target={'_blank'} to={`/estabelecimento/${data?.establishment?.slug}/detalhes`}>
                                <ContentOrganizer>
                                    <DynamicProfileImage size='24px' img={data?.establishment?.profile_image_id?.thumb} word={data?.establishment?.name} />
                                    <Text color={colors.nightDark} paddingBottom={'0'} marginLeft={'10px'} marginRight={'5px'}>Organizado por</Text>
                                    <Text color={colors.nightDark} type='bold' paddingBottom={'0px'}>{data?.establishment?.name}</Text>
                                </ContentOrganizer>
                            </LinkPage>
                        </ContainerOrganizerLink>
                    )
                }


                {
                    data?.origin === textIa && (
                        <EventOrigin verticalCenter marginBottom='15px'>
                            <StyledIa>
                                <Icon name='smartToy' color={colors.night} />
                            </StyledIa>

                            <Text paddingBottom={'0'} color={colors.nightDark} marginLeft={'10px'}>Evento mapeado através de IA</Text>

                            <InfoTooltip
                                bgColor={colors.night}
                                text=<>O Whizr usa inteligência artificial para o mapeamento de eventos que não são cadastrados através de parceiros oficiais.
                                    <br></br>
                                    Recomendamos checar as informações no local oficial do evento.</>
                                type='info'
                            />
                        </EventOrigin>
                    )
                }

                {
                    participants?.length > 0 && (
                        <ParticipantsSection>
                            <ParticipantsAvatars>
                                {participants.map((participant, index) => (
                                    <AvatarWrapper key={participant.id} index={index}>
                                        <DynamicProfileImage
                                            size='36px'
                                            img={participant.avatar_thumb}
                                            word={participant.first_name}
                                        />
                                    </AvatarWrapper>
                                ))}
                            </ParticipantsAvatars>
                            <Text name='small' color={colors.nightDark} paddingBottom={'0'}>
                                <b>{participants[0]?.first_name}</b>
                                {participants.length > 1 && <> e mais {participants.length - 1} pessoa{participants.length - 1 > 1 ? 's' : ''}</>}
                                {' '}já garanti{participants.length > 1 ? 'ram' : 'u'} presença
                            </Text>
                        </ParticipantsSection>
                    )
                }

            </CardMainContent >

        </Container>
    )
}


const Container = styled(Column)`
    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row-reverse;
        align-items: center;
        justify-content: center;
        padding: 30px 0 30px 20px;
        position: relative;
        overflow: hidden;
    }
`;

const BluredBackground = styled.div`
    display: none;
    @media screen and (min-width: ${breakpoints.lg}){
        display: flex;
        width: 200%;
        height: calc(100% + 30px);
        position: absolute;
        left: -10%;
        top: -2%;
        background-image: url(${props => props.src});
        background-size: cover;
        background-position: center;
        filter: blur(10px);
        z-index: -1;

        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: ${colors.nightDark};
            opacity: .75;  
        }
    }
`;


const StyledBackground = styled.img`
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 0 0 18px 18px;
    
    @media screen and (min-width: ${breakpoints.lg}) {
        width: 85%;
        height: 280px;
        border-radius: 12px;
        box-shadow: none;
    }
`;

const ImageContainer = styled.div`
    width: 100%;
    height: auto;
    position: relative;
`;

const CategoriesInfos = styled(Row)`
    width: 100%;
    align-items: center;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
    margin-bottom: 16px;

    & > p {
        padding-bottom: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        margin-bottom: 10px;    
        justify-content: flex-start;
    }
    
`;

const IconContainer = styled.div`
    @media screen and (min-width: ${breakpoints.lg}) {
        svg {
            stroke: ${colors.neutral} !important;
        }
    }
`

const CardMainContent = styled(Column)`
    padding: 16px 6%;
    justify-content: center;
    align-items: center;   
    width: 100%; 

    & > h1 {
        margin-top: 6px;
        margin-bottom: 4px;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        align-items: flex-start;
    }
`

const EventName = styled.div`
    @media screen and (min-width: ${breakpoints.lg}) {
        h2 {
            color: ${colors.neutral} !important;
            font-size: ${fonts.sizes.hero} !important;
        }
    }
`

const EventDate = styled.div`
    @media screen and (min-width: ${breakpoints.lg}) {
        p {
            color: ${colors.neutral} !important;
        }
    }
`

const EventOrigin = styled(Row)`
    @media screen and (min-width: ${breakpoints.lg}) {
        p {
            color: ${colors.neutral} !important;
        }
    }
`

const EventType = styled.div`
    color: ${colors.neutralLight};
    border-radius: 30px;
    padding: 4px 10px;
    font-size: ${fonts.sizes.small};
    text-transform: capitalize;
    margin-top: 8px;

    ${({ type }) => type === eventTypes.free.slug && `
        background: ${eventTypes.free.color};
    `}

    ${({ type }) => type === eventTypes.paid.slug && `
        background: ${eventTypes.paid.color};
    `}
`;


const Tag = styled(FullyCentered)`
    padding: 4px 8px;
    border-radius: 30px;
    background: ${colors.occasionalOrange};
    width: auto;
    margin-left: 4px;
    margin-top: 8px;

    ${({ adulthood }) => adulthood && `
        background: ${colors.nightDark};
    `};

    & > p {
        padding-bottom: 0 !important;
        text-transform: capitalize !important;
    }
`;

const ContentInfoRow = styled(Row)`
    margin-bottom: 10px;
    align-items: flex-start;
    text-align: left;

    ${({ address }) => address && `
        align-items: center;
    `}
    
    ${({ single }) => single && `
        align-items: center;

        & > p {
            padding-bottom: 0 !important;
        }
    `}

    &:nth-child(3){
        margin-bottom: 0;
    }

    & > p {
        padding: 0;
        margin: 0;
    }

    & > a > p{
        padding-bottom: 0 !important;
    }
`;

const ContentLinkAddress = styled.div`
    width: auto;

    @media screen and (min-width: ${breakpoints.lg}) {
        p {
            color: ${colors.neutral} !important;
        }
    }
`;

const DateContainer = styled.div`
    height: auto;
    margin-left: 10px;
`;

const OnlyOnedate = styled.div`
    height: 100%;
    margin-top: 8px;
    
    @media screen and (min-width: ${breakpoints.lg}) {
        p {
            color: ${colors.neutral} !important;
        }
    }
`;

const ContainerOrganizerLink = styled(Row)`
    width: 100%;
`

const ContentOrganizer = styled(Row)`
    width: 100%;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 15px;
    text-align: left;

    transition: all ease-in-out .25s;
    &:hover {
        transform: scale(1.01)
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        p {
            color: ${colors.neutral} !important;
        }
    }
`;

const CronogramContainer = styled.div`
    width: 100%;
`;

const StyledIa = styled.div`
    width: 26px;
    height: 26px;

    @media screen and (min-width: ${breakpoints.lg}) {
        svg {
            stroke: ${colors.neutral} !important;
            fill: ${colors.neutral} !important;
        }
    }
`;

const ParticipantsSection = styled(Row)`
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;

    @media screen and (min-width: ${breakpoints.lg}) {
        p {
            color: ${colors.neutral} !important;
        }
    }
`;

const ParticipantsAvatars = styled(Row)`
    width: auto;
    flex-shrink: 0;
`;

const AvatarWrapper = styled.div`
    margin-left: ${({ index }) => index === 0 ? '0' : '-10px'};
    border-radius: 50%;
    border: 2px solid ${colors.neutralLight};
    z-index: ${({ index }) => 6 - index};

    @media screen and (min-width: ${breakpoints.lg}) {
        border-color: ${colors.nightDark};
    }
`;

const FinishedEvent = styled(FullyCentered)`
    position: absolute;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 0 0 18px 18px;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 85%;
        height: 280px;
        border-radius: 12px;
    }

    &:before{ 
        content: '';
        background-color: ${colors.nightDark};
        color: ${colors.neutral};
        opacity: .8;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2;
        border-radius: 0 0 18px 18px;
        
        @media screen and (min-width: ${breakpoints.lg}) {
            border-radius: 12px;
        }
    }
    
    &:after{ 
        content: 'Evento encerrado';
        background-color: ${colors.danger};
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        font-size: ${fonts.sizes.subtitle};
        color: ${colors.neutral};
        z-index: 3;
    }
`;
